import { Box, Typography, Tooltip, Stack, Chip, debounce } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import NoPrgramFound from '../../assets/no-program-found.svg';
import LoaderComponent from 'components/Loader';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { defaultOptions, getDefaultOptions, getChartOptions } from './chartWidgetUtility';
import { useCacheDataController } from 'Context/contexts/CacheDataContext';
import { useEffect, useRef, useState } from 'react';
import { BadgeData, ChartWidgetProps } from './type';
import { formatDateAndValue, formatDateAndValueForIbp } from '../../utils/formateValue';
import { renderTooltipIcon } from 'components/TableauObs/common/KpiCard';
import { InfoTooltip } from './InfoTooltip';

const ChartWidget = ({
	badge,
	chartBgColor = '#1b1e35',
	bgColor,
	titleColor,
	lineColor,
	isDataEstateTrend = false,
	isDataEstateQuery = false,
	conversionUnitProp = null,
	chartInterval = 0,
	isLoading = false,
	isOverlayWidget = false,
	isInProgress = false,
	title = '',
	tooltipLabel = '',
	data,
	footer,
	minTitleHeight = '50.5px',
	hasBadge = true,
	lastUpdatedDate = '',
	handleEvent,
	restrictToMillions = false,
	isDatabrick = false,
	isForeCast = false,
	isYearly = false,
	isFinops = false,
	isHappySignal = false,
	lastYearAvgData = [],
	hasNoFormat = false,
	relevantYear = new Date().getFullYear(),
	themeMode = 'dark',
}: Readonly<ChartWidgetProps>) => {
	const customColor = (chartBgColor && chartBgColor !== '#1b1e35') ? chartBgColor : bgColor;
	const hasCustomBg = !!customColor || isDataEstateTrend;
	const isLightTrend = themeMode === 'lightTrend' || hasCustomBg;
	const activeBgColor = customColor ?? (isLightTrend ? '#FFFFFF' : 'primary.main');
	const [controller] = useCacheDataController();
	const { conversionUnit } = controller;
	const [windowSize, setWindowSize] = useState(window.innerWidth);
	const [gridLeft, setGridLeft] = useState('5%');
	const chartContainerRef = useRef<HTMLDivElement>(null);

	/* -------------------- Helpers -------------------- */

	const calculateGridLeft = (width: number): string => {
		if (isLightTrend) {
			if (width < 420) return '16%';
			if (width < 600) return '14%';
			if (width < 820) return '12%';
			return '10%';
		}
		if (width < 420) return '18%';
		if (width < 600) return '15%';
		if (width < 820) return '12%';
		if (width < 920) return '9%';
		if (width < 1260) return '7%';
		return '5%';
	};

	const buildTooltipFormatter = () => {
		return function (params: any) {
			if (Array.isArray(params)) {
				const validParams = params.filter((item: any) => item && item.value && (Array.isArray(item.value) ? item.value[1] != null : item.value != null));
				if (validParams.length === 0) return '';
				return validParams
					.map((item) => {
						const isAverage =
							item.seriesName &&
							item.seriesName.toLowerCase().includes('average') &&
							isHappySignal;

						if (isAverage) {
							return `${Number(relevantYear) - 1} Average: ${item.value[1]}`;
						}

						return hasNoFormat
							? formatDateAndValueForIbp(item.value)
							: formatDateAndValue(
									item.value,
									restrictToMillions,
									title,
									isDatabrick,
									isYearly
							  );
					})
					.join('<br/>');
			}

			return formatDateAndValue(
				params.value,
				restrictToMillions,
				title,
				false,
				isYearly
			);
		};
	};

	const resolveBadgeData = (): BadgeData | null => {
		if (badge) return badge;

		if (badgePercentage) {
			return {
				badgeLabel: `${Math.abs(Number(badgePercentage.toFixed(2)))}% / YEAR`,
				badgeArrow: Math.sign(badgePercentage) === -1 ? 'down' : 'up',
			};
		}

		return null;
	};

	const buildAdjustedOption = (): any => {
		if (!chartOption || Object.keys(chartOption).length === 0) {
			return getDefaultOptions(isLightTrend);
		}

		const baseOption = {
			...chartOption,
			...(customColor && {
				backgroundColor: customColor,
			}),
			grid: {
				...chartOption.grid,
				left: gridLeft,
				...(customColor && {
					backgroundColor: customColor,
				}),
			},
		};

		const shouldApplyTooltip =
			(conversionUnitProp == null || conversionUnitProp === 'cost') && !isLightTrend && !isDataEstateTrend;

		if (!shouldApplyTooltip) return baseOption;

		return {
			...baseOption,
			tooltip: {
				...chartOption.tooltip,
				formatter: buildTooltipFormatter(),
			},
		};
	};

	useEffect(() => {
		const handleResize = debounce(() => {
			if (!chartContainerRef.current) return;

			const containerWidth = chartContainerRef.current.offsetWidth;

			setGridLeft(calculateGridLeft(containerWidth));
			setWindowSize(window.innerWidth);
		}, 200);

		handleResize();
		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, [themeMode]);

	const result = getChartOptions({
		chartData: data,
		selectedByte: conversionUnitProp,
		conversionUnit,
		chartInterval,
		lastUpdatedDate,
		isDatabrick,
		isForeCast,
		isYearly,
		isFinops,
		isHappySignal,
		lastYearAvgData,
		hasNoFormat,
		relevantYear,
		themeMode: isLightTrend ? 'lightTrend' : themeMode,
		isDataEstateTrend,
		isDataEstateQuery,
		lineColor,
		title,
	});

	const { chartOption, badgePercentage, chartTooltip } = result;

	const badgeData: BadgeData | null = resolveBadgeData();
	const adjustedOption = buildAdjustedOption();

	return (
		<Box display="flex" flexGrow={1} height="100%" position="relative">
			<Stack
				minHeight="100px"
				p={isLightTrend ? 2.5 : 2}
				bgcolor={activeBgColor}
				fontFamily="Montserrat"
				borderRadius="5px"
				flexGrow={1}
				sx={{
					...((isLightTrend || hasCustomBg) && {
						boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.04)',
					}),
					...(handleEvent && { onClick: !isFinops ? handleEvent : undefined, sx: { cursor: !isFinops ? 'pointer' : 'default' } }),
				}}
			>
				<Stack direction="row" gap={2} justifyContent="space-between" alignItems="center" minHeight={minTitleHeight}>
					<Stack direction="row" gap={1.5} alignItems="center">
						<Typography
							variant={isOverlayWidget ? 'overlayWidgetTitle' : 'widgetTitle'}
							onClick={isFinops ? handleEvent : undefined}
							style={{ cursor: isFinops ? 'pointer' : 'default' }}
							sx={{
								fontWeight: (hasCustomBg || isLightTrend) ? '500 !important' : (hasNoFormat ? '500 !important' : ''),
								color: titleColor ? titleColor : ((hasCustomBg || isLightTrend) ? '#000000 !important' : 'inherit'),
								fontSize: isLightTrend ? '1.15rem !important' : undefined,
							}}
						>
							{title}
						</Typography>
						{(!!tooltipLabel || !!chartTooltip || isDataEstateTrend) && (
							<InfoTooltip
								label={
									typeof (tooltipLabel || chartTooltip || 'Data trend over time') === 'string' ? (
											tooltipLabel || chartTooltip || 'Data trend over time'
									) : (
										tooltipLabel || chartTooltip
									)
								}
							/>
						)}
					</Stack>
					{!isLoading && hasBadge && badgeData && (
						<Chip
							variant="chartBadge"
							sx={{
								backgroundColor: isLightTrend ? '#0B2545 !important' : '#009BD8 !important',
								color: '#FFFFFF !important',
								borderRadius: isLightTrend ? '16px !important' : undefined,
								height: isLightTrend ? '28px' : undefined,
								px: isLightTrend ? 0.5 : undefined,
								'& .MuiChip-label': isLightTrend
									? {
											fontWeight: 700,
											fontSize: '11px',
											color: '#FFFFFF !important',
									  }
									: undefined,
								'& .MuiChip-icon': isLightTrend
									? {
											color: '#FFFFFF !important',
											fontSize: '16px !important',
									  }
									: undefined,
							}}
							icon={badgeData.badgeArrow === 'up' ? <ArrowUpwardIcon style={{ color: '#fff' }} /> : <ArrowDownwardIcon style={{ color: '#fff' }} />}
							label={badgeData.badgeLabel}
							title={badgeData.badgeTitle}
						/>
					)}
				</Stack>
				<Stack minHeight="350px" flexGrow={1} justifyContent="center" key={windowSize} ref={chartContainerRef}>
					{!isLoading ? (
						<ReactECharts
							option={adjustedOption}
							style={{ width: '100%', height: '100%' }}
							opts={{ renderer: 'canvas', width: 'auto' }}
							notMerge={true}
							lazyUpdate={true}
						/>
					) : (
						<LoaderComponent />
					)}
				</Stack>
				{isInProgress && (
					<Box position="absolute" right={15} bottom={10}>
						<Tooltip title="In Progress/Validation">
							<img
								src={NoPrgramFound}
								style={{
									height: '20px',
									width: '20px'
								}}
							/>
						</Tooltip>
					</Box>
				)}
			</Stack>
		</Box>
	);
};

export default ChartWidget;

